<template>
  <ButtonCard
    class="button"
    theme="neutral-50"
    aspect="14-13"
    stroke="m"
    :corner-banner-text="category.cornerBannerText"
    :corner-banner-color="category.cornerBannerColor"
    :disabled="disabled"
    :icon-scale-to-fit="
      mMedias.get(category.mediaIcon) &&
      mMedias.get(category.mediaIcon).scaleToFit
    "
    @focus="handleMouseOver"
    @blur="handleMouseLeave"
    @mouseover="handleMouseOver"
    @mouseleave="handleMouseLeave"
  >
    <template #icon>
      <GraphicMedia
        v-if="
          mMedias.get(category.mediaIcon) &&
          mMedias.get(category.mediaIcon).scaleToFit
        "
        :style="style"
        :media="mMedias.get(category.mediaIcon)"
        :size="4"
      />
      <GraphicMask
        v-if="
          mMedias.get(category.mediaIcon) &&
          !mMedias.get(category.mediaIcon).scaleToFit &&
          displayType !== 'center_circle' &&
          parentDisplayType !== 'center_circle' &&
          !mMedias.get(category.mediaIcon).doNotForceColorization
        "
        :style="style"
        :src="
          mMedias.get(category.mediaIcon).urlCompressed ||
          mMedias.get(category.mediaIcon).url
        "
        :size="4.35"
        :color="colorIcon"
      />
      <GraphicIcon
        v-else-if="
          mMedias.get(category.mediaIcon) &&
          !mMedias.get(category.mediaIcon).scaleToFit
        "
        :style="style"
        :src="
          mMedias.get(category.mediaIcon).urlCompressed ||
          mMedias.get(category.mediaIcon).url
        "
        :size="4.35"
      />
    </template>
    <span class="button-title">{{ category.title }}</span>
  </ButtonCard>
</template>
<script lang="ts" setup>
interface Props {
  category: CategorySchema;
  parentDisplayType?:
    | 'default'
    | 'center_circle'
    | 'dimensionTest_and_result'
    | 'sponsor'
    | 'shortcuts'
    | 'additional';
  disabled?: boolean;
  mMedias: Map<string, MediaSchema>;
}

const props = defineProps({
  category: null,
  parentDisplayType: { default: 'default' },
  disabled: { type: Boolean, default: false },
  mMedias: null
});

const available = defineModel<boolean>('available', {
  default: true,
});

const isScaleToFit = computed(() => {
  return (
    props.mMedias.get(props.category.mediaIcon) &&
    props.mMedias.get(props.category.mediaIcon).scaleToFit
  );
});

const spring = computed(() => {
  if (isScaleToFit.value) {
    return {
      damping: 20,
      stiffness: 100,
      mass: 0.96,
    };
  } else {
    return { damping: 14, stiffness: 240, mass: 0.96 };
  }
});

const { style, scale } = useScaleEffect(
  1,
  false,
  spring.value.damping,
  spring.value.stiffness,
  spring.value.mass
);

const { $colors } = useNuxtApp();

const colorIcon = computed(() => {
  if (props.category.cornerBannerText) return props.category.cornerBannerColor;
  if (
    props.category.displayColorCode === '#FFFFFF' ||
    !props.category.displayColorCode
  ) {
    return $colors.secondary;
  }
  return props.category.displayColorCode;
});

const displayType = computed(() =>
  props.category.displayType ? props.category.displayType : null
);

const handleMouseOver = () => {
  scale.value = isScaleToFit.value ? 1.04 : 1.15;
};
const handleMouseLeave = () => {
  scale.value = 1;
};

const csscolor = computed(() => colorIcon.value);
</script>
<style lang="scss" scoped>
.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.button {
  transition: border 500ms $ease-in-quad, box-shadow 500ms $ease-in-quad;
  .button-title {
    display: -webkit-box;
    overflow: hidden;
    transition: color 400ms $ease-in-quad;
    color: var(--color_neutral_800);
    text-overflow: ellipsis;
    white-space: normal;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    @include font('s', 700, 'primary');
  }

  @include hover-focus-desktop {
    transition: border 500ms $ease-in-quad, box-shadow 500ms $ease-out-quad;
    border: var(--stroke_thickness_m) solid var(--color_neutral_100);
    box-shadow: rgb(0 0 0 / 10%) 0 1px 3px 0, rgb(0 0 0 / 6%) 0 1px 2px 0;

    .button-title {
      color: v-bind(csscolor);
    }
  }
}
</style>
